/**
 * Local Keycloak (Cognito replacement) auth helper.
 *
 * Enabled when VITE_AUTH_PROVIDER === 'keycloak'. Uses the OAuth2 Resource
 * Owner Password (direct grant) flow against the local Keycloak realm so the
 * existing email/password login form keeps working without AWS Cognito.
 *
 * NOTE: embedding the confidential client secret in the SPA is acceptable for
 * the LOCAL MVP only. For real deployments use Authorization Code + PKCE.
 *
 * Tokens are kept in localStorage (key `kc_tokens`). The QSE backend reads the
 * Authorization header verbatim, so callers send the RAW access token.
 */

const STORAGE_KEY = 'kc_tokens'

interface KcTokens {
  access_token: string
  refresh_token?: string
  expires_at: number // epoch ms
}

const env = (k: string, fallback = ''): string => {
  try {
    return (import.meta as any).env?.[k] ?? fallback
  } catch {
    return fallback
  }
}

export const isKeycloakAuth = (): boolean => {
  const provider = (env('VITE_AUTH_PROVIDER') || '').toLowerCase()
  if (provider === 'keycloak' || provider === 'oidc') return true
  // Packaging alias: onprem defaults to Keycloak when AUTH_PROVIDER unset
  if (!provider && (env('VITE_DEPLOYMENT_MODE') || '').toLowerCase() === 'onprem') {
    return true
  }
  return false
}

const kcConfig = () => {
  const configured = env('VITE_KEYCLOAK_URL', '').trim().replace(/\/$/, '')
  const realm = env('VITE_KEYCLOAK_REALM', 'qse-mvp')
  // Empty URL → same-origin /realms/* (nginx proxies to Keycloak). Required for
  // HTTPS subdomains; a baked http://IP:18081 URL is mixed-content blocked.
  const prefix = configured ? `${configured}/realms/${realm}` : `/realms/${realm}`

  return {
    realm,
    clientId: env('VITE_KEYCLOAK_CLIENT_ID', 'qse-mvp-client'),
    clientSecret: env('VITE_KEYCLOAK_CLIENT_SECRET', 'qse-mvp-client-secret'),
    tokenUrl: `${prefix}/protocol/openid-connect/token`,
    logoutUrl: `${prefix}/protocol/openid-connect/logout`,
  }
}

const readTokens = (): KcTokens | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)

    return raw ? (JSON.parse(raw) as KcTokens) : null
  } catch {
    return null
  }
}

const writeTokens = (data: any): KcTokens => {
  const tokens: KcTokens = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    // refresh ~30s before expiry
    expires_at: Date.now() + Math.max(0, (data.expires_in ?? 300) - 30) * 1000,
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))

  return tokens
}

const requestToken = async (params: Record<string, string>): Promise<any> => {
  const { tokenUrl } = kcConfig()
  const body = new URLSearchParams(params)

  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })

  if (!res.ok) {
    let detail = ''

    try {
      const j = await res.json()

      detail = j.error_description || j.error || ''
    } catch {
      // ignore
    }

    throw new Error(`Keycloak token request failed (${res.status}) ${detail}`)
  }

  return res.json()
}

/** Direct-grant login. Stores tokens and returns the access token. */
export const keycloakLogin = async (username: string, password: string): Promise<string> => {
  const { clientId, clientSecret } = kcConfig()
  const data = await requestToken({
    grant_type: 'password',
    client_id: clientId,
    client_secret: clientSecret,
    username,
    password,
    scope: 'openid',
  })

  return writeTokens(data).access_token
}

const refreshToken = async (refresh: string): Promise<string | null> => {
  try {
    const { clientId, clientSecret } = kcConfig()
    const data = await requestToken({
      grant_type: 'refresh_token',
      client_id: clientId,
      client_secret: clientSecret,
      refresh_token: refresh,
    })

    return writeTokens(data).access_token
  } catch (err) {
    console.warn('Keycloak token refresh failed:', err)
    localStorage.removeItem(STORAGE_KEY)

    return null
  }
}

/**
 * Tokens for opening QPrime without a second login. The refresh token is
 * reusable (Keycloak refresh rotation is off), so the dashboard session stays
 * valid after QPrime exchanges a copy.
 */
export const getKeycloakHandoff = async (): Promise<{
  accessToken: string
  refreshToken?: string
} | null> => {
  const accessToken = await getKeycloakToken()

  if (!accessToken) return null

  return {
    accessToken,
    refreshToken: readTokens()?.refresh_token,
  }
}

/** Return a valid access token (refreshing if needed), or null if not logged in. */
export const getKeycloakToken = async (): Promise<string | null> => {
  const tokens = readTokens()

  if (!tokens) return null

  if (Date.now() < tokens.expires_at) {
    return tokens.access_token
  }

  if (tokens.refresh_token) {
    return refreshToken(tokens.refresh_token)
  }

  localStorage.removeItem(STORAGE_KEY)

  return null
}

export const keycloakLogout = async (): Promise<void> => {
  const tokens = readTokens()

  localStorage.removeItem(STORAGE_KEY)

  if (tokens?.refresh_token) {
    try {
      const { clientId, clientSecret, logoutUrl } = kcConfig()

      await fetch(logoutUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: clientId,
          client_secret: clientSecret,
          refresh_token: tokens.refresh_token,
        }).toString(),
      })
    } catch {
      // best effort
    }
  }
}
